import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, mergeProps as _mergeProps, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "pb-4 print:hidden" }
const _hoisted_2 = { class: "flex justify-end gap-2 pb-2 print:hidden" }
const _hoisted_3 = { class: "text-gray-700 text-sm" }
const _hoisted_4 = { class: "text-gray-900 text-sm font-medium" }
const _hoisted_5 = { class: "text-gray-700 text-sm" }
const _hoisted_6 = { class: "text-gray-700 text-sm" }
const _hoisted_7 = { class: "text-gray-700 text-sm" }
const _hoisted_8 = {
  key: 0,
  class: "flex flex-wrap gap-1"
}
const _hoisted_9 = {
  key: 1,
  class: "text-gray-400"
}
const _hoisted_10 = { class: "p-8" }
const _hoisted_11 = {
  class: "text-center py-16",
  "data-cy": "clients-empty"
}

import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import type { PropType } from 'vue';
import { storeToRefs } from 'pinia';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import LlDataTable from '@/components/LlDataTable.vue';
import LlDataTablePagination from '@/components/LlDataTablePagination.vue';
import LlIcon from '@/components/LlIcon.vue';
import LlMenu from '@/components/LlMenu.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlUserAvatar from '@/components/LlUserAvatar.vue';
import { useExportUtilities } from '@/composables/useExportUtilities';
import { useNavbarHeight } from '@/composables/useNavbarHeight';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import type { SortByItem } from '@/models/llDataTableTypes';
import RouteService from '@/services/RouteService';
import type { UiRouterState } from '@/services/RouteService/UiRouterState';
import { useAccountsStore } from '@/services/UserService/useAccountsStore';
import { useMeStore } from '@/services/UserService/useMeStore';
import { formatOrDefault } from '@/services/utils';
import ClientsFilters from './ClientsFilters.vue';
import { QB_STATUS_TOOLTIP } from '../constants/clientsListConstants';
import type { ClientRow, ClientsOrderBy } from '../models/clientsListTypes';
import { useClientsListStore } from '../stores/useClientsListStore';
import { formatContactName } from '../utils/clientsListHelpers';


export default /*@__PURE__*/_defineComponent({
  __name: 'ClientsTable',
  props: {
  state: { type: Object as PropType<UiRouterState>, required: false },
},
  setup(__props) {

const props = __props;

const clientsListStore = useClientsListStore();
const { filters, clients, totalCount, loading, exporting } = storeToRefs(clientsListStore);
const meStore = useMeStore();
const { me, terms, permissions, company, access, settings, features } = storeToRefs(meStore);
const accountsStore = useAccountsStore();
const { accountsList } = storeToRefs(accountsStore);

const { handleError, handleSuccess, handleWarning } = useSnackbarHandler();
const { exportCSV } = useExportUtilities();
const routeService = new RouteService(props);
const navbarHeight = useNavbarHeight();

const deleteConfirmRef = ref<{ open: () => Promise<boolean> } | null>(null);
const clientToDelete = ref<ClientRow | null>(null);

const matterTerm = computed(() => terms.value?.Matter || 'Matter');

// The legacy page's gates: its QuickBooks panel needs matters.accounting, and the
// validate button inside it also needs billing access on a connected company.
const canViewInQuickbooks = computed(() => !!permissions.value?.matters?.accounting);
const canValidateQuickbooks = computed(
  () => canViewInQuickbooks.value && !!company.value?.integration?.quickbooks && !!access.value?.billing,
);
const usesNewTimeEntry = computed(
  () => !!features.value?.timeEntriesTab && !!settings.value?.enableNewTimeEntry,
);

const PRINT_HIDDEN = { class: 'print:hidden' };

const headers = computed(() => [
  // Only once the connection check has run; before that every cell would be blank.
  ...(filters.value.checkQuickbooks
    ? [{ title: '', key: 'qbo', sortable: false, width: 30, headerProps: PRINT_HIDDEN, cellProps: PRINT_HIDDEN }]
    : []),
  { title: 'Client ID', key: 'clientId', value: 'reference', sortable: true, width: 100 },
  { title: 'Client', key: 'clientName', value: 'name', sortable: true },
  { title: 'Company', key: 'companyName', sortable: true },
  { title: 'Contact', key: 'contactName', sortable: true },
  { title: 'Phone', key: 'phone', sortable: true },
  ...(filters.value.orphans ? [] : [{ title: 'Responsible', key: 'primary', sortable: true }]),
  { title: '', key: 'actions', sortable: false, width: 50, headerProps: PRINT_HIDDEN, cellProps: PRINT_HIDDEN },
]);

const sortBy = computed<SortByItem[]>(() => [
  { key: filters.value.orderBy, order: filters.value.sortAscending ? 'asc' : 'desc' },
]);

const loadClients = async () => {
  try {
    await clientsListStore.loadClients();
  } catch (error) {
    handleError(error);
  }
};

const loadCustomFields = async () => {
  try {
    await clientsListStore.loadCustomFields();
  } catch (error) {
    handleError(error);
  }
};

const handleSortChange = (newSort: SortByItem[]) => {
  if (!newSort.length) return;
  filters.value.orderBy = newSort[0].key as ClientsOrderBy;
  filters.value.sortAscending = newSort[0].order === 'asc';
  filters.value.page = 1;
  loadClients();
};

const handlePageChange = (page: number) => {
  filters.value.page = page;
  loadClients();
};

const handlePageSizeChange = (pageSize: number) => {
  filters.value.pageSize = pageSize;
  filters.value.page = 1;
  loadClients();
};

const handleClearFilters = () => {
  clientsListStore.resetFilters();
  loadClients();
};

const handleValidateQuickbooks = () => {
  filters.value.checkQuickbooks = true;
  filters.value.page = 1;
  loadClients();
};

const handlePrint = () => {
  window.print();
};

const handleExport = async () => {
  try {
    const rows = await clientsListStore.fetchAllClients();
    await exportCSV(
      rows,
      {
        'Client Id': 'reference',
        Client: 'name',
        Company: 'companyName',
        Contact: (item: ClientRow) => formatContactName(item.contactName),
        Phone: 'phone',
        Responsible: (item: ClientRow) => item.primary.join(' '),
      },
      { filename: 'Clients' },
    );
  } catch (error) {
    handleError(error);
  }
};

const handleCreateClient = () => {
  routeService.go(usesNewTimeEntry.value ? 'clients2.client2' : 'clients2.newMatter.client', {
    methodOverride: 'client page',
  });
};

const createMatterFor = (item: ClientRow) => {
  routeService.go(usesNewTimeEntry.value ? 'clients2.matter2' : 'clients2.newMatter', {
    clientId: item.id,
  });
};

const openClient = (item: ClientRow) => {
  // The client page assumes at least one matter and breaks without one.
  if (filters.value.orphans) {
    handleWarning(`Please create a ${terms.value?.matter || 'matter'} for the client to start using it in the system`);
    return;
  }
  routeService.go('clients2.client', { id: item.id });
};

const handleRowClick = (event: MouseEvent, { item }: { item: ClientRow }) => {
  if ((event.target as HTMLElement).closest('button, a')) return;
  openClient(item);
};

const openInQuickbooks = (item: ClientRow) => {
  const base = me.value?.configuration?.quickbooks;
  if (!base || !item.quickBooksId) return;
  window.open(
    `${base}/app/customerdetail?nameId=${encodeURIComponent(item.quickBooksId)}`,
    '_blank',
    'noopener,noreferrer',
  );
};

const deleteConfirmMessage = computed(() =>
  clientToDelete.value
    ? `Should the client "${clientToDelete.value.label}" be removed from the system? It doesn't have any ${terms.value?.matter || 'matter'}s or other data associated with it, but it cannot be undone.`
    : '',
);

const confirmDelete = async (item: ClientRow) => {
  clientToDelete.value = item;
  const confirmed = await deleteConfirmRef.value?.open();
  if (!confirmed) return;
  try {
    await clientsListStore.deleteClient(item.id);
    handleSuccess(`The client "${item.label}" was removed`);
    await loadClients();
  } catch (error) {
    handleError(error);
  }
};

const rowMenuItems = (item: ClientRow) => {
  if (filters.value.orphans) {
    return [
      {
        id: 'create-matter',
        title: `Create ${matterTerm.value}`,
        dataCy: 'client-row-create-matter',
        action: () => createMatterFor(item),
      },
      { id: 'delete', title: 'Delete', dataCy: 'client-row-delete', action: () => confirmDelete(item) },
    ];
  }
  return [
    { id: 'open', title: 'Open', dataCy: 'client-row-open', action: () => openClient(item) },
    ...(item.quickBooksId && canViewInQuickbooks.value
      ? [
        {
          id: 'quickbooks',
          title: 'View in QuickBooks',
          dataCy: 'client-row-view-quickbooks',
          action: () => openInQuickbooks(item),
        },
      ]
      : []),
  ];
};

onMounted(async () => {
  try {
    await Promise.all([meStore.getMe(), accountsStore.listAccounts()]);
  } catch (error) {
    handleError(error);
  }
  const ownAccount = (accountsList.value || []).find((account: { id: string }) => account.id === me.value?.id);
  clientsListStore.applyRoleDefaults(me.value?.role, ownAccount);
  loadClients();
  if (features.value?.customFieldFilters) {
    loadCustomFields();
  }
});

onBeforeUnmount(() => {
  clientsListStore.reset();
});

return (_ctx: any,_cache: any) => {
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(ClientsFilters, {
        onApplyFilters: loadClients,
        onClearFilters: handleClearFilters
      })
    ]),
    _createElementVNode("div", {
      class: "sticky z-[3] bg-white print:static",
      style: _normalizeStyle({ top: `${_unref(navbarHeight)}px` })
    }, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(LlSecondaryButton, {
          variant: "outlined",
          "append-icon": "mdi-printer",
          "data-cy": "clients-print-button",
          onClick: handlePrint
        }, {
          default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
            _createTextVNode(" Print ", -1)
          ]))]),
          _: 1
        }),
        _createVNode(LlSecondaryButton, {
          variant: "outlined",
          "append-icon": "mdi-download",
          disabled: _unref(exporting) || _unref(loading),
          "data-cy": "clients-export-button",
          onClick: handleExport
        }, {
          default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
            _createTextVNode(" Export ", -1)
          ]))]),
          _: 1
        }, 8, ["disabled"]),
        (canValidateQuickbooks.value)
          ? (_openBlock(), _createBlock(LlSecondaryButton, {
              key: 0,
              variant: "outlined",
              "append-icon": "mdi-cloud-sync-outline",
              loading: _unref(loading),
              disabled: _unref(loading),
              "data-cy": "clients-validate-quickbooks-button",
              onClick: handleValidateQuickbooks
            }, {
              default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                _createTextVNode(" Validate QuickBooks Connection ", -1)
              ]))]),
              _: 1
            }, 8, ["loading", "disabled"]))
          : _createCommentVNode("", true),
        _createVNode(LlSecondaryButton, {
          color: "primary",
          variant: "flat",
          "data-cy": "create-client-button",
          onClick: handleCreateClient
        }, {
          default: _withCtx(() => [
            _createVNode(LlIcon, { class: "mr-1" }, {
              default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                _createTextVNode("mdi-plus", -1)
              ]))]),
              _: 1
            }),
            _cache[4] || (_cache[4] = _createTextVNode(" Create Client ", -1))
          ]),
          _: 1
        })
      ])
    ], 4),
    _createVNode(LlDataTable, {
      headers: headers.value,
      items: _unref(clients),
      "items-length": _unref(totalCount),
      loading: _unref(loading),
      "sort-by": sortBy.value,
      "item-value": "id",
      "data-cy": "clients-table",
      class: "[&_.v-data-table__th]:!bg-neutral-50 [&_.v-data-table__th]:!font-semibold [&_.v-data-table__th]:!normal-case [&_.v-data-table__th]:!tracking-normal [&_.v-data-table__th]:!text-[0.8125rem] [&_.v-data-table__th]:!text-gray-600 [&_.v-data-table__th]:!px-4 [&_.v-data-table__th]:!py-3.5 [&_.v-data-table__th]:!border-b [&_.v-data-table__th]:!border-gray-200 [&_.v-data-table__td]:!px-4 [&_.v-data-table__td]:!py-3.5 [&_.v-data-table__td]:!text-[0.8125rem] [&_.v-data-table__td]:!text-gray-900 [&_.v-data-table__td]:!border-b [&_.v-data-table__td]:!border-gray-100 [&_tbody_tr]:cursor-pointer [&_tbody_tr:hover]:!bg-gray-50 print:[&_.v-table__wrapper]:!h-auto print:[&_.v-table__wrapper]:!overflow-visible",
      "onUpdate:sortBy": handleSortChange,
      "onClick:row": handleRowClick
    }, {
      [`header.qbo`]: _withCtx(() => [
        _createVNode(LlTooltip, {
          text: _unref(QB_STATUS_TOOLTIP),
          icon: "help"
        }, null, 8, ["text"])
      ]),
      [`item.qbo`]: _withCtx(({ item }) => [
        (item.quickBooksId)
          ? (_openBlock(), _createBlock(LlIcon, {
              key: 0,
              icon: "mdi-circle",
              color: item.goodQBConnection ? 'success' : 'error',
              size: "10"
            }, null, 8, ["color"]))
          : _createCommentVNode("", true)
      ]),
      [`item.clientId`]: _withCtx(({ item }) => [
        _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(formatOrDefault)(item.reference)), 1)
      ]),
      [`item.clientName`]: _withCtx(({ item }) => [
        _createElementVNode("span", _hoisted_4, _toDisplayString(item.name), 1)
      ]),
      [`item.companyName`]: _withCtx(({ item }) => [
        _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(formatOrDefault)(item.companyName, '—')), 1)
      ]),
      [`item.contactName`]: _withCtx(({ item }) => [
        _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(formatOrDefault)(_unref(formatContactName)(item.contactName), '—')), 1)
      ]),
      [`item.phone`]: _withCtx(({ item }) => [
        _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(formatOrDefault)(item.phone, '—')), 1)
      ]),
      [`item.primary`]: _withCtx(({ item }) => [
        (item.primary.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item.primary, (initials) => {
                return (_openBlock(), _createBlock(LlUserAvatar, {
                  key: initials,
                  initials: initials
                }, null, 8, ["initials"]))
              }), 128))
            ]))
          : (_openBlock(), _createElementBlock("span", _hoisted_9, "—"))
      ]),
      [`item.actions`]: _withCtx(({ item }) => [
        _createVNode(LlMenu, {
          location: "bottom end",
          items: rowMenuItems(item)
        }, {
          activator: _withCtx(({ props: menuProps }) => [
            _createVNode(LlSecondaryButton, _mergeProps(menuProps, {
              icon: "mdi-dots-vertical",
              variant: "text",
              size: "small",
              density: "comfortable",
              "aria-label": `Actions for ${item.name}`,
              "data-cy": "client-row-menu"
            }), null, 16, ["aria-label"])
          ]),
          _: 2
        }, 1032, ["items"])
      ]),
      loading: _withCtx(() => [
        _createElementVNode("div", _hoisted_10, [
          _createVNode(_component_v_skeleton_loader, {
            type: "table-row@10",
            class: "w-full"
          })
        ])
      ]),
      "no-data": _withCtx(() => [
        _createElementVNode("div", _hoisted_11, [
          _createVNode(LlIcon, {
            icon: "mdi-account-outline",
            size: "64",
            color: "grey-lighten-2",
            class: "mb-4"
          }),
          _cache[5] || (_cache[5] = _createElementVNode("p", { class: "text-xl font-medium text-gray-600 mb-2" }, "No clients match the filters", -1))
        ])
      ]),
      _: 2
    }, 1032, ["headers", "items", "items-length", "loading", "sort-by"]),
    _createVNode(LlDataTablePagination, {
      sticky: "",
      class: "print:hidden",
      page: _unref(filters).page,
      "page-size": _unref(filters).pageSize,
      "total-count": _unref(totalCount),
      "onUpdate:page": handlePageChange,
      "onUpdate:pageSize": handlePageSizeChange
    }, null, 8, ["page", "page-size", "total-count"]),
    _createVNode(LlConfirmationDialog, {
      ref_key: "deleteConfirmRef",
      ref: deleteConfirmRef,
      title: "Delete this client?",
      message: deleteConfirmMessage.value,
      "confirm-text": "Delete",
      "show-warning-icon": false,
      "confirm-color": "error",
      "confirm-variant": "flat",
      "data-cy": "client-delete-dialog"
    }, null, 8, ["message"])
  ]))
}
}

})